import {
  FilterOptionKeys,
  GoalFilterDocument,
  GoalGroupBy,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import {
  goalListSortOptions,
  goalSortingModeTranslationKeys,
  projectFilterSections,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDeadline16 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { useUser } from 'shared/hooks/use-user';
import { GoalSortingMode } from 'shared/types/sorting-mode';

// kept in sync with GoalFilterDocument's filter keys in @griply/types
type GoalFilterOptionKey =
  | FilterOptionKeys.LifeArea
  | FilterOptionKeys.Goal
  | FilterOptionKeys.GoalActive
  | FilterOptionKeys.Date
  | FilterOptionKeys.Deadline
  | FilterOptionKeys.GoalImpact
  | FilterOptionKeys.GoalType;

// embedded lists are bound to one container, so life-area sorting is meaningless there
const embeddedSortingModes = goalListSortOptions.filter(
  (mode) => mode !== GoalSortingMode.LifeArea,
);

// the caller picks the grouping/sort/filter sets for its surface; the rest is shared
export type ProjectsFilterButtonProps = {
  filterDocument: GoalFilterDocument;
  defaultFilterDocument: GoalFilterDocument;
  updateFilterDocument: (document: GoalFilterDocument) => void;
  onReset: () => void;
  supportedGroupByModes: GoalGroupBy[];
  supportedFilters: GoalFilterOptionKey[];
  supportedSortingModes?: GoalSortingMode[];
};

export const ProjectsFilterButton: React.FC<ProjectsFilterButtonProps> = ({
  filterDocument,
  defaultFilterDocument,
  updateFilterDocument,
  onReset,
  supportedGroupByModes,
  supportedFilters,
  supportedSortingModes,
}) => {
  const { t } = useTranslation();

  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();

  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const tags = useSortedTags();

  const sortDirection =
    user?.settings?.projectSortDirection ?? OrderDirection.Asc;
  const onSortDirectionChange = (direction: OrderDirection) =>
    updateUserSettings({ projectSortDirection: direction });

  const labelOverrides = useMemo(
    () => ({
      [GroupByMode.Goal]: t('general.labels.projects.group.parent-goal'),
      [GroupByMode.Deadline]: t('general.labels.goals.sort.options.deadline'),
    }),
    [t],
  );

  const filterLabelOverrides = useMemo(
    () => ({ [FilterOptionKeys.Date]: t('general.labels.start-date') }),
    [t],
  );

  const filterIconOverrides = useMemo(
    () => ({ [FilterOptionKeys.Deadline]: CalendarDeadline16 }),
    [],
  );

  return (
    <FilterButton
      filterDocument={filterDocument}
      defaultFilterDocument={defaultFilterDocument}
      goals={goals}
      lifeAreas={lifeAreas}
      tags={tags}
      updateFilterDocument={updateFilterDocument}
      supportedGroupByModes={supportedGroupByModes}
      supportedSortingModes={supportedSortingModes ?? embeddedSortingModes}
      supportedFilters={supportedFilters}
      sortingTranslationKeys={goalSortingModeTranslationKeys}
      sortDirection={sortDirection}
      onSortDirectionChange={onSortDirectionChange}
      labelOverrides={labelOverrides}
      filterLabelOverrides={filterLabelOverrides}
      filterIconOverrides={filterIconOverrides}
      filterSections={projectFilterSections}
      title={t('context-menus.filtering.project.title')}
      onReset={onReset}
      preventDrag
      renderAsIconButton
    />
  );
};
